import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const LOCATIONS: Record<string, string> = {};

interface Device {
  id: string;
  name: string;
  status: string;
  locationId: string;
  locationName: string;
  battery?: number;
  charging?: boolean;
  wifi?: string;
  model?: string;
}

function parseDevice(raw: any): Device {
  const attrs = raw.attributes || {};
  const appComp = (raw.components || []).find((c: any) => c.type === 'APPLICATION');
  const battComp = (raw.components || []).find((c: any) => c.type === 'BATTERY');
  const wifiComp = (raw.components || []).find((c: any) => c.type === 'WIFI');
  const sessionLoc = appComp?.applicationDetails?.sessionLocation || '';

  return {
    id: raw.id,
    name: attrs.name || raw.id,
    status: raw.status?.category || 'UNKNOWN',
    locationId: sessionLoc,
    locationName: LOCATIONS[sessionLoc] || sessionLoc,
    battery: battComp?.batteryDetails?.visiblePercent,
    charging: battComp?.batteryDetails?.externalPower === 'AVAILABLE_CHARGING',
    wifi: wifiComp?.wifiDetails?.ssid,
    model: attrs.model,
  };
}

function StatusBadge({ status }: { status: string }) {
  const isAvailable = status === 'AVAILABLE';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: isAvailable ? 'rgba(40,167,69,0.1)' : 'rgba(108,117,125,0.1)',
        color: isAvailable ? '#28a745' : '#6c757d',
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          backgroundColor: isAvailable ? '#28a745' : '#6c757d',
        }}
      />
      {status}
    </span>
  );
}

export function Terminals() {
  const { user } = useAuth();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [pairingLocationId, setPairingLocationId] = useState<number>(user?.locationId || 1);
  const [pairingName, setPairingName] = useState('');
  const [pairingCode, setPairingCode] = useState('');
  const [pairingCodeId, setPairingCodeId] = useState('');
  const [pairingLoading, setPairingLoading] = useState(false);
  const [pairingError, setPairingError] = useState('');
  const [showPairing, setShowPairing] = useState(false);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      // Load locations for name mapping (non-fatal — requires admin)
      try {
        const locResult = await api.getSquareLocations();
        (locResult.locations || []).forEach((l: any) => {
          LOCATIONS[l.id] = l.name;
        });
      } catch {
        // Non-admin users can't access /square/locations — skip name mapping
      }

      const result = await api.getSquareDevices(user?.locationId || undefined);
      const parsed = (result.devices || []).map(parseDevice);
      // Sort: AVAILABLE first, then by name
      parsed.sort((a: Device, b: Device) => {
        if (a.status === 'AVAILABLE' && b.status !== 'AVAILABLE') return -1;
        if (b.status === 'AVAILABLE' && a.status !== 'AVAILABLE') return 1;
        return a.name.localeCompare(b.name);
      });
      setDevices(parsed);
    } catch {
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // Poll pairing status
  useEffect(() => {
    if (!pairingCodeId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const result = await api.getDeviceCodeStatus(pairingCodeId);
        if (cancelled) return;
        if (result.status === 'PAIRED') {
          setPairingCode('');
          setPairingCodeId('');
          setShowPairing(false);
          loadDevices();
        } else if (result.status === 'EXPIRED') {
          setPairingError('Pairing code expired. Please generate a new one.');
          setPairingCode('');
          setPairingCodeId('');
        }
      } catch {
        if (!cancelled) {
          setPairingError('Lost connection while checking pairing status.');
        }
      }
    };
    const interval = setInterval(poll, 3000);
    poll();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [pairingCodeId, loadDevices]);

  const handlePair = async () => {
    setPairingLoading(true);
    setPairingError('');
    try {
      const result = await api.createDeviceCode(pairingLocationId, pairingName || undefined);
      setPairingCode(result.code);
      setPairingCodeId(result.id);
    } catch (err: any) {
      setPairingError(err.message || 'Failed to generate pairing code.');
    } finally {
      setPairingLoading(false);
    }
  };

  if (!user || (user.role !== 'ADMIN' && user.role !== 'BARBER')) {
    return <p style={{ padding: 24, color: 'var(--color-text-muted)' }}>Staff access required.</p>;
  }

  const availableCount = devices.filter((d) => d.status === 'AVAILABLE').length;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-family-heading)',
              fontWeight: 700,
              fontSize: 22,
              color: 'var(--color-text)',
              margin: '0 0 4px',
            }}
          >
            Square Terminals
          </h1>
          <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: 0 }}>
            {devices.length} terminal{devices.length !== 1 ? 's' : ''} paired
            {availableCount > 0 && (
              <>
                {' '}
                · <span style={{ color: '#28a745' }}>{availableCount} available</span>
              </>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={loadDevices}
            disabled={loading}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid var(--color-border)',
              backgroundColor: '#fff',
              color: 'var(--color-text-muted)',
              fontSize: 13,
              cursor: 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Refreshing...' : '↻ Refresh'}
          </button>
          <button
            onClick={() => {
              setShowPairing(true);
              setPairingCode('');
              setPairingCodeId('');
              setPairingError('');
            }}
            style={{
              padding: '8px 18px',
              borderRadius: 8,
              border: 'none',
              background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
              color: '#D9B256',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            + Pair New Terminal
          </button>
        </div>
      </div>

      {/* Pairing Panel */}
      {showPairing && (
        <div
          style={{
            marginBottom: 24,
            padding: 24,
            border: '1px solid var(--color-border)',
            borderRadius: 12,
            backgroundColor: '#fff',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <h2
              style={{
                fontFamily: 'var(--font-family-heading)',
                fontWeight: 700,
                fontSize: 16,
                color: 'var(--color-text)',
                margin: 0,
              }}
            >
              Pair a Square Terminal
            </h2>
            <button
              onClick={() => {
                setShowPairing(false);
                setPairingCode('');
                setPairingCodeId('');
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 18,
                color: 'var(--color-text-muted)',
              }}
            >
              ✕
            </button>
          </div>

          {pairingCode ? (
            /* Show the code */
            <div style={{ textAlign: 'center' }}>
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--color-text-muted)',
                  marginBottom: 12,
                  lineHeight: 1.6,
                }}
              >
                On your Square Terminal, go to
                <br />
                <strong>Sign in → Use a Device code</strong>
                <br />
                and enter this code:
              </p>
              <div
                style={{
                  fontSize: 40,
                  fontWeight: 800,
                  letterSpacing: '0.2em',
                  color: '#D9B256',
                  fontFamily: 'var(--font-family-heading)',
                  padding: '18px 0',
                  marginBottom: 16,
                  background: 'rgba(217,178,86,0.08)',
                  borderRadius: 12,
                }}
              >
                {pairingCode}
              </div>
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    border: '2px solid rgba(217,178,86,0.25)',
                    borderTopColor: '#D9B256',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
                <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                  Waiting for terminal to connect...
                </span>
              </div>
            </div>
          ) : (
            /* Config form */
            <div>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--color-text-muted)',
                      marginBottom: 4,
                    }}
                  >
                    Location
                  </label>
                  {user?.locationId ? (
                    <div
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: '1px solid var(--color-border)',
                        fontSize: 13,
                        backgroundColor: 'var(--color-background)',
                        color: 'var(--color-text)',
                      }}
                    >
                      {user.locationId === 1 ? 'Nundah' : 'Mitchelton'}
                    </div>
                  ) : (
                    <select
                      value={pairingLocationId}
                      onChange={(e) => setPairingLocationId(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: '1px solid var(--color-border)',
                        fontSize: 13,
                        backgroundColor: 'var(--color-background)',
                      }}
                    >
                      <option value={1}>Nundah</option>
                      <option value={2}>Mitchelton</option>
                    </select>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--color-text-muted)',
                      marginBottom: 4,
                    }}
                  >
                    Terminal Name (optional)
                  </label>
                  <input
                    type="text"
                    value={pairingName}
                    onChange={(e) => setPairingName(e.target.value)}
                    placeholder="e.g. Front Counter"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--color-border)',
                      fontSize: 13,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
              {pairingError && (
                <p style={{ fontSize: 12, color: '#dc3545', marginBottom: 12 }}>{pairingError}</p>
              )}
              <button
                onClick={handlePair}
                disabled={pairingLoading}
                style={{
                  padding: '10px 24px',
                  borderRadius: 8,
                  border: 'none',
                  background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                  color: '#D9B256',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: pairingLoading ? 'default' : 'pointer',
                  opacity: pairingLoading ? 0.7 : 1,
                }}
              >
                {pairingLoading ? 'Generating...' : 'Generate Pairing Code'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Device List */}
      {loading ? (
        <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 40 }}>
          Loading terminals...
        </p>
      ) : devices.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '48px 24px',
            border: '1px solid var(--color-border)',
            borderRadius: 12,
            backgroundColor: '#fff',
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 12 }}>📱</div>
          <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)', marginBottom: 6 }}>
            No terminals paired yet
          </p>
          <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>
            Connect a Square Terminal to accept card payments.
          </p>
          <button
            onClick={() => setShowPairing(true)}
            style={{
              padding: '10px 24px',
              borderRadius: 8,
              border: 'none',
              background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
              color: '#D9B256',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            + Pair Your First Terminal
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {devices.map((device) => (
            <div
              key={device.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '14px 18px',
                border: '1px solid var(--color-border)',
                borderRadius: 10,
                backgroundColor: '#fff',
                transition: 'border-color 0.15s',
              }}
            >
              {/* Icon */}
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background:
                    device.status === 'AVAILABLE'
                      ? 'rgba(217,178,86,0.1)'
                      : 'rgba(108,117,125,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                📱
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)' }}>
                    {device.name}
                  </span>
                  <StatusBadge status={device.status} />
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--color-text-muted)',
                    display: 'flex',
                    gap: 12,
                  }}
                >
                  <span>📍 {device.locationName}</span>
                  {device.model && <span>· {device.model}</span>}
                  {device.wifi && <span>· WiFi: {device.wifi}</span>}
                </div>
              </div>

              {/* Battery */}
              {device.battery != null && (
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: device.battery < 20 ? '#dc3545' : 'var(--color-text)',
                    }}
                  >
                    {device.charging ? '⚡' : '🔋'} {device.battery}%
                  </div>
                </div>
              )}

              {/* Device ID (abbreviated) */}
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--color-text-muted)',
                  fontFamily: 'monospace',
                  flexShrink: 0,
                }}
              >
                {device.id.replace('device:', '').slice(0, 8)}…
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
